import { type Ref, ref, watchEffect } from 'vue'
import { type ThemeDTO } from '../memorize/memorize'
import { useLocalStorage } from '@vueuse/core'
import {
  CACHE_KEY_PREVIEW_THEME,
  CACHE_KEY_SELECT_THEME,
  CACHE_KEY_SELECT_THEME_SERVICE_ID,
  STORAGE_KEY_MUSIC,
  STORAGE_KEY_THEME,
  CACHE_KEY_SHOW_MEMORIZE_THEME_DIALOG
} from '@/constants'
import { hexToRgba } from '@/utils/color-util'
import { useUserLocalStorage } from '../user/users'

// memorize
// 是否展示中元节弹窗
const _showMemorizeThemeDialog = useLocalStorage<boolean>(CACHE_KEY_SHOW_MEMORIZE_THEME_DIALOG, true)
export const showMemorizeThemeDialog = _showMemorizeThemeDialog

// 当前主题
const _currentTheme = ref<string>('default')
export const currentTheme = _currentTheme

export function setTheme(theme: string) {
  _currentTheme.value = theme
  document.documentElement.setAttribute('data-theme', theme)
}

export const themeServiceId = useLocalStorage(CACHE_KEY_SELECT_THEME_SERVICE_ID, '')
export const selectedTheme: Ref<Record<string, ThemeDTO>> = useLocalStorage(CACHE_KEY_SELECT_THEME, {})
export const selectedThemeOrder = useLocalStorage<string[]>(`${CACHE_KEY_SELECT_THEME}-order`, [])
export const previewTheme: Ref<Record<string, ThemeDTO>> = useLocalStorage(CACHE_KEY_PREVIEW_THEME, {})
const SELECTED_THEME_LRU_WINDOW = 5

const normalizeSelectedThemeLru = () => {
  const selectedThemeKeys = Object.keys(selectedTheme.value || {})
  if (selectedThemeKeys.length === 0) {
    selectedThemeOrder.value = []
    return
  }
  let order = selectedThemeOrder.value.filter((serviceId) => selectedThemeKeys.includes(serviceId))
  if (order.length === 0) {
    order = [...selectedThemeKeys]
  } else {
    selectedThemeKeys.forEach((serviceId) => {
      if (!order.includes(serviceId)) {
        order.push(serviceId)
      }
    })
  }
  while (order.length > SELECTED_THEME_LRU_WINDOW) {
    const evictedServiceId = order.shift()
    if (evictedServiceId) {
      delete selectedTheme.value[evictedServiceId]
    }
  }
  const orderSet = new Set(order)
  Object.keys(selectedTheme.value).forEach((serviceId) => {
    if (!orderSet.has(serviceId)) {
      delete selectedTheme.value[serviceId]
    }
  })
  selectedThemeOrder.value = order
}

if (import.meta.client) {
  normalizeSelectedThemeLru()
}

export const setSelectedThemeByServiceId = (serviceId: string, theme: ThemeDTO) => {
  if (!serviceId) {
    return
  }
  selectedTheme.value[serviceId] = theme
  const order = selectedThemeOrder.value.filter((item) => item !== serviceId)
  order.push(serviceId)
  while (order.length > SELECTED_THEME_LRU_WINDOW) {
    const evictedServiceId = order.shift()
    if (evictedServiceId) {
      delete selectedTheme.value[evictedServiceId]
    }
  }
  selectedThemeOrder.value = order
}

export const gDefaultThemeColors = {
  main: '#fff',
  dark: '#000',
  light: '#fefefe',
  gray: '#808080',
  white: '#fff'
} as unknown as ThemeDTO['themeColors']

export const defaultTheme = {
  id: '215796174643400704',
  gifFile: null,
  gifSmallFile: null,
  file: null,
  smallFile: null,
  themeType: 1,
  mode: 1,
  orderParam: 1,
  name: 'white_color',
  colorValue: '#FFFFFF',
  memberOnly: false,
  outdated: false,
  toDefault: false,
  themeColors: {
    main: '#3D3D3D',
    dark: '#171717',
    light: '#F3F3F3',
    gray: '#B1B1B1',
    white: '#FEFEFE'
  },
  createdAt: 1720403060337
} as unknown as ThemeDTO

export const gThemeNow = () => useState<ThemeDTO>('theme-now', () => defaultTheme)

export const useThemeSystem = () => {
  watchEffect(() => {
    // Priority: Preview Theme > Selected Theme > Default Theme
    const currentServiceId = themeServiceId.value
    if (currentServiceId) {
      const preview = previewTheme.value[currentServiceId]
      const selected = selectedTheme.value[currentServiceId]
      if (preview) {
        gThemeNow().value = preview
      } else if (selected) {
        gThemeNow().value = selected
      }
    }

    if (gThemeNow().value && typeof document !== 'undefined') {
      const html = document.documentElement
      const isLight = gThemeNow().value.mode === 1
      html.setAttribute('data-theme', isLight ? 'light' : 'dark')

      const colors = gThemeNow().value.themeColors as any
      if (colors && colors.main) {
        const newTheme = {
          '--theme-main': colors.main,
          '--theme-dark': colors.dark,
          '--theme-light': colors.light,
          '--theme-light-80': hexToRgba(colors.light, 0.8),
          '--theme-light-0': hexToRgba(colors.light, 0),
          '--theme-gray': colors.gray,
          '--theme-white': colors.white
        }
        Object.entries(newTheme || {}).forEach(([k, v]) => html.style.setProperty(k, v as string))
      }
    }
  })
}

export function useMusicSettings() {
  const musicSetting = useUserLocalStorage<Record<string, boolean>>(STORAGE_KEY_MUSIC, () => getDefaultMusicSetting())
  return musicSetting
}

export function getDefaultMusicSetting(): Record<string, boolean> {
  return {}
}
